import Link from 'next/link';

import { Box, Button, Text } from '@zillow/constellation-10';

import {
  useRentalManagerUserPropertyDetailsWebApiHandler_overview,
  useRentalManagerUserPropertySummaryWebApiHandler_listing,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useScreenSize } from '../../../../hooks/useScreenSize';
import accessUtils from '../../../../utils/accessUtils';

import { PRODUCT_TYPE_NAME_TO_ID } from '../../../../constants/FreemiumConstants';
import { LISTING_SUMMARY_DISPLAY_STATUS } from '../../../../constants/ListingSummaryConstants';
import { getCreateListingRoute, getListingRoute, getPremiumAnalyticsRoute } from '../../../../constants/urls';
import { RELISTING_STEP_SUFFIX, STEP_DELIMITER, STEP_KEYS } from '../../../CreateListingPage/constants/stepKeys';
import { getActiveProductTypeFreeFeatures, getActiveProductTypePremiumFeatures } from './constants';

import { FeatureCard } from './components/FeatureCard';

import { useAuth } from '../../../AuthProvider';

type Props = {
  listingAlias: string;
  isActive: boolean;
  isActiveProductTypeFree: boolean;
  isActiveProductTypePremium: boolean;
  onUpgradeClick: () => void;
};

const FeaturesOfPremium = ({
  listingAlias,
  isActive,
  isActiveProductTypeFree,
  isActiveProductTypePremium,
  onUpgradeClick,
}: Props) => {
  const { user } = useAuth();
  const { isScreenDesktop } = useScreenSize();
  const { data: overviewData } = useRentalManagerUserPropertyDetailsWebApiHandler_overview({ listingAlias });
  const { data: listingSummaryData } = useRentalManagerUserPropertySummaryWebApiHandler_listing({ listingAlias });

  const freemiumProducts = overviewData?.freemium?.products ?? [];
  // @ts-expect-error recommendation is not typed on the overview DTO
  const recsCount: string | null = overviewData?.recommendation?.unlockedCount ?? null;

  const upgradedProduct = freemiumProducts.find((p) => p.type === PRODUCT_TYPE_NAME_TO_ID.PREMIUM);
  const premiumPriceAndDuration = upgradedProduct ? `${upgradedProduct.price} for ${upgradedProduct.duration}` : null;

  let { step } = listingSummaryData?.modularListingProgress || {};
  if (step === `${STEP_KEYS.REVIEW}${STEP_DELIMITER}${RELISTING_STEP_SUFFIX}`.toLowerCase()) {
    step = step.split(STEP_DELIMITER)[0];
  }

  const canResumeModularListingCreation =
    step !== undefined &&
    accessUtils.canAccessModularListing({
      isMultifamily: overviewData?.isMultifamily ?? false,
      listingTypeCode: overviewData?.listingType?.code ?? 0,
      lastActivatedMs: listingSummaryData?.lastActivatedMs,
      userCanAccessModularListing: user?.features?.canAccessModularListing ?? false,
    });

  const displayStatus = listingSummaryData?.syndication?.overview?.displayStatus?.toLowerCase() ?? '';

  const isNotActiveAndFree = !isActive && isActiveProductTypeFree;
  const isNotActiveAndPremium = !isActive && isActiveProductTypePremium;
  const isActiveAndFree = isActive && isActiveProductTypeFree;
  const isActiveAndPremium = isActive && isActiveProductTypePremium;

  const EXPLORE_PREMIUM_BENEFITS = 'Explore your premium benefits';

  let heading = '';
  let bodyText: string | null = null;
  let ctaLabel: string | null = null;
  let isPaymentCTA = false;

  if (isNotActiveAndFree) {
    heading = 'Get more from your listing with premium';
    bodyText = premiumPriceAndDuration;
    ctaLabel = 'List your Property';
  } else if (isNotActiveAndPremium) {
    heading = EXPLORE_PREMIUM_BENEFITS;
    ctaLabel = 'View your customized data';
  } else if (isActiveAndFree) {
    heading = 'Get more from your listing with premium';
    bodyText = premiumPriceAndDuration;
    ctaLabel = 'Upgrade to premium';
    isPaymentCTA = true;
  } else if (isActiveAndPremium) {
    heading = EXPLORE_PREMIUM_BENEFITS;
    ctaLabel = 'View your customized data';
  }

  let ctaHref = null;
  if (!isPaymentCTA) {
    if (isNotActiveAndFree) {
      const useCreateListing =
        canResumeModularListingCreation || displayStatus === LISTING_SUMMARY_DISPLAY_STATUS.INACTIVE;
      ctaHref = useCreateListing ? getCreateListingRoute({ listingAlias }) : getListingRoute({ listingAlias });
    } else if (isNotActiveAndPremium || isActiveAndPremium) {
      ctaHref = getPremiumAnalyticsRoute({ listingAlias });
    }
  }

  const bodyContent = (
    <Box
      css={{
        // oxlint-disable-next-line constellation/prefer-semantic-tokens -- ESLint's pandaLint set this warn-only; oxlint's oxlintPandaLint sets it error, no code change
        backgroundColor: '{colors.blue.100}',
        padding: { base: '{spacing.1200} {spacing.400}', lg: '{spacing.1200} {spacing.800}' },
      }}
    >
      <Box
        css={{
          display: 'flex',
          justifyContent: 'center',
          flexDirection: 'column',
          alignItems: 'center',
          marginBottom: '600',
        }}
      >
        <Text textStyle="heading-sm" asChild css={{ marginBottom: '400' }}>
          <h4>{heading}</h4>
        </Text>
        <Text textStyle="body-bold" asChild>
          <p>The edge you need to fill your vacancy fast</p>
        </Text>
        {bodyText && (
          <Text asChild>
            <p>{bodyText}</p>
          </Text>
        )}
      </Box>
      <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', columnGap: '600', rowGap: '400' }}>
        {isActiveProductTypePremium
          ? getActiveProductTypePremiumFeatures().map((item, index) => {
              const { heading: featureHeading, body, Icon, modalContent } = item;
              const headingCount = featureHeading === 'Listing recommendations' ? recsCount : null;
              return (
                <FeatureCard
                  key={index}
                  heading={featureHeading}
                  headingCount={headingCount}
                  body={body}
                  Icon={Icon}
                  modalContent={modalContent}
                />
              );
            })
          : getActiveProductTypeFreeFeatures().map((item, index) => {
              const { heading: featureHeading, body, Icon, modalContent } = item;
              return (
                <FeatureCard key={index} heading={featureHeading} body={body} Icon={Icon} modalContent={modalContent} />
              );
            })}
      </Box>
      <Box css={{ display: 'flex', justifyContent: 'center' }}>
        {ctaLabel && (
          <Button
            tone="brand"
            emphasis="filled"
            css={{ marginTop: '1200' }}
            asChild={Boolean(ctaHref)}
            onClick={isPaymentCTA ? onUpgradeClick : undefined}
          >
            {ctaHref ? <Link href={ctaHref}>{ctaLabel}</Link> : ctaLabel}
          </Button>
        )}
      </Box>
    </Box>
  );

  return isScreenDesktop ? bodyContent : <Box css={{ marginTop: { base: '-32px', xl: '0' } }}>{bodyContent}</Box>;
};

export default FeaturesOfPremium;
